import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import GoogleAuthButton from './GoogleAuthButton';
import Navbar from '../shared/Navbar';

const getSafeRedirect = (redirect: string | null) => {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) return '/dashboard';
  return redirect;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = getSafeRedirect(searchParams.get('redirect'));
  const onGoogleSuccess = useCallback(() => {
    navigate(redirectTarget);
  }, [navigate, redirectTarget]);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        if (me?.authenticated) {
          navigate(redirectTarget);
        }
      } catch {}
    })();
  }, [navigate, redirectTarget]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResendMessage(null);
    setLoading(true);
    try {
      await api.login(email, password);
      navigate(redirectTarget);
    } catch (e: any) {
      const message = e?.message || 'Login failed';
      setError(message);
      setNeedsVerification(message === 'Verify your email before signing in');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendMessage(null);
    try {
      const result = await api.resendVerification(email);
      setResendMessage(result.message);
    } catch (e: any) {
      setError(e?.message || 'Failed to resend verification email');
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container auth-container">
          <div className="tm-surface auth-card">
            <h2 className="auth-title">Sign In</h2>
            <p className="auth-subtitle">Access your lessons and progress</p>
            <form onSubmit={onSubmit} className="login-form">
            <div className="form-row">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-row">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              <div className="auth-links auth-links--end">
                <Link to="/forgot-password">Forgot my password</Link>
              </div>
            </div>
            {error && <div className="ml-error tm-mb-sm">{error}</div>}
            {needsVerification && (
              <button
                type="button"
                className="tm-button tm-button--primary ml-replace-btn"
                onClick={resendVerification}
              >
                Resend verification email
              </button>
            )}
            {resendMessage && <div className="auth-subtitle">{resendMessage}</div>}
            <button type="submit" className="tm-button tm-button--primary ml-replace-btn auth-submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <div className="auth-separator"><span>or</span></div>
            <GoogleAuthButton
              text="signin_with"
              onSuccess={onGoogleSuccess}
              onError={setError}
            />
            <div className="auth-links">
              <Link to={`/register?redirect=${encodeURIComponent(redirectTarget)}`}>Need an account? Register</Link>
              <span className="auth-divider">•</span>
              <Link to="/">Back to Home</Link>
            </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
